import { Box, Autocomplete, TextField } from '@mui/material';
import { colors, Typography } from '@sweep-io/sweep-design';
import { useCallback, useState, useEffect } from 'react';
import { TeamsRecipientInfo, TeamsRecipientType, TeamsRecipientStruct } from '../teams/types';
import { useIntegrations } from '../../../hooks/useIntegrations';
import {
  NestedFieldsField,
  NestedFieldsSelector,
} from '../../common/fieldsSelectors/NestedFieldsSelector';
import { autocompleteStyles } from './styles';
import { StyledChip } from '../../common/StyledChip';
import teamsPng from '../images/teamsIcon.png';
import sfPng from '../images/sfIcon.png';
import CloseIcon from '@mui/icons-material/Close';
import { parseSweepFieldPolymorphicID } from '../../common/fieldsSelectors/nestedFieldsSelectorHelper';
import { ChevronDown } from '@sweep-io/sweep-design/dist/icons/ChevronDown';
import { SfdcDisplayType } from '../../../types/fields';

interface TeamsMembersSelectorProps {
  onChange: (members: TeamsRecipientInfo[]) => void;
  readonly?: boolean;
  customButtonText?: string;
  teamId?: string;
  crmOrgId: string;
  objectName: string;
  selectedMembers?: TeamsRecipientStruct[];
}

export const TeamsMembersSelector = ({
  onChange,
  readonly,
  customButtonText = 'Add Users / Teams',
  teamId,
  crmOrgId,
  objectName,
  selectedMembers = [],
}: TeamsMembersSelectorProps) => {
  const [members, setMembers] = useState<TeamsRecipientInfo[]>([]);
  const [selectedValues, setSelectedValues] = useState<TeamsRecipientInfo[]>([]);
  const { getTeamMembers } = useIntegrations();

  const fetchMembers = useCallback(async () => {
    if (!teamId) return;
    const membersList = await getTeamMembers(teamId);
    setMembers(membersList);
  }, [teamId, getTeamMembers]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  useEffect(() => {
    const convertedMembers = selectedMembers.map((member) => {
      const memberId =
        typeof member.value === 'string' ? member.value : member.value?.fieldIds[0] || '';

      // For Salesforce fields, use the stored label or field ID as fallback
      if (
        member.type === TeamsRecipientType.USER_STORED_IN_SALESFORCE_FIELD ||
        member.type === TeamsRecipientType.CHANNEL_STORED_IN_SALESFORCE_FIELD
      ) {
        const converted = {
          id: memberId,
          label: member.label || memberId || '',
          type: member.type,
        };
        return converted;
      }

      // For Teams members, try to find the member in the fetched members to get the label
      const apiMember = members.find((m) => {
        const apiMemberId = Array.isArray(m.id) ? m.id[0] : m.id;
        return apiMemberId === memberId;
      });

      const converted = {
        id: memberId,
        label: member.label || apiMember?.label || '',
        type: member.type,
      };
      return converted;
    });
    setSelectedValues(convertedMembers);
  }, [selectedMembers, members]);

  const handleChange = useCallback(
    (_: any, newValues: TeamsRecipientInfo[]) => {
      setSelectedValues(newValues);
      onChange(newValues);
    },
    [onChange],
  );

  const handleSalesforceFieldSelect = (sweepField: NestedFieldsField) => {
    const newMember: TeamsRecipientInfo = {
      id: sweepField.fieldIds,
      label: sweepField?.fieldLabels?.join('.'),
      type:
        sweepField?.sfDisplayType === SfdcDisplayType.STRING
          ? TeamsRecipientType.CHANNEL_STORED_IN_SALESFORCE_FIELD
          : TeamsRecipientType.USER_STORED_IN_SALESFORCE_FIELD,
    };
    const updatedValues = [...selectedValues, newMember];
    handleChange(null, updatedValues);
  };

  const returnChipWithProps = useCallback(
    (option: TeamsRecipientInfo) => {
      const ChipProps = {
        key: Array.isArray(option.id) ? option.id[0] : option.id,
        label: option.label,
        title: option.label,
        size: 'small' as const,
        icon: (
          <img
            src={
              option.type === TeamsRecipientType.USER_STORED_IN_SALESFORCE_FIELD ? sfPng : teamsPng
            }
            alt={`${option.type} icon`}
            style={{ width: '12px', marginLeft: '8px' }}
          />
        ),
        onDelete: !readonly
          ? () => {
              const newValues = selectedValues.filter((v) => v.id !== option.id);
              handleChange(null, newValues);
            }
          : undefined,
        deleteIcon: <CloseIcon sx={{ color: '#000', fontSize: 12 }} />,
      };

      return <StyledChip {...ChipProps} sx={{ textTransform: 'capitalize' }} />;
    },
    [readonly, selectedValues, handleChange],
  );

  const filterByEndSelectionUser = (field: SweepField) => {
    const { isResolvable } = parseSweepFieldPolymorphicID(field.id || '');
    if (isResolvable) {
      return true;
    }
    if (field.sfDisplayType === SfdcDisplayType.REFERENCE) {
      return Boolean(field.objectNames?.includes('User'));
    }
    return false;
  };

  return (
    <Box sx={autocompleteStyles}>
      <Autocomplete
        disablePortal
        disabled={readonly || !teamId}
        multiple
        options={members}
        value={selectedValues}
        onChange={handleChange}
        getOptionLabel={(option) => option.label}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={!teamId ? 'Select a team first' : customButtonText}
            variant="outlined"
            fullWidth
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Typography variant="body">{option.label}</Typography>
          </Box>
        )}
        renderTags={(value) => value.map((option) => returnChipWithProps(option))}
        isOptionEqualToValue={(option, value) => {
          if (!value) return false;
          const optionId = Array.isArray(option.id) ? option.id[0] : option.id;
          const valueId = Array.isArray(value.id) ? value.id[0] : value.id;
          return optionId === valueId;
        }}
      />

      {objectName && (
        <Box>
          <NestedFieldsSelector
            filterBy={filterByEndSelectionUser}
            readonly={readonly || !teamId}
            displayTypesFilter={SfdcDisplayType.EMAIL}
            crmOrgId={crmOrgId ?? ''}
            objectType={objectName ?? ''}
            nestedPath={[]}
            customButtonEndIcon={<ChevronDown size="medium" color={colors.blue[500]} />}
            useCustomButton
            customButtonText={'Add from Salesforce field'}
            customButtonSx={{
              marginTop: '8px',
              height: '20px',
              color: colors.blue[500],
              border: 'none',
              textTransform: 'unset',
              p: '4px 0px',
              '& span.MuiTypography-root': {
                fontSize: '14px',
                fontWeight: 500,
              },
              '&:hover': {
                background: 'transparent',
                color: colors.blue[600],
              },
            }}
            onChange={handleSalesforceFieldSelect}
          />
        </Box>
      )}
    </Box>
  );
};
